import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import StudentDashboard from './pages/StudentDashboard';
import UploadPage from './pages/UploadPage';
import Login from './pages/Login';
// LoggingDashboard removed - logging is now hidden from users
import axios from 'axios';

// API URL will be loaded from config.json
let API_URL = 'http://localhost:5000/api'; // Default for local development

// Restore persistent session from localStorage on page load
const savedToken = localStorage.getItem('authToken');
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

function App() {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiConfigLoaded, setApiConfigLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!savedToken);

  // Auto-logout when the backend rejects the token
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !error.config?.url?.endsWith('/login')) {
          localStorage.removeItem('authToken');
          delete axios.defaults.headers.common['Authorization'];
          setIsAuthenticated(false);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Load API URL from config.json on startup
  useEffect(() => {
    loadApiConfig();
  }, []);

  // Load statistics once API config is loaded and user is authenticated
  useEffect(() => {
    if (apiConfigLoaded && isAuthenticated) {
      fetchStatistics();
    }
  }, [apiConfigLoaded, isAuthenticated]);

  const loadApiConfig = async () => {
    try {
      const response = await fetch('/config.json');
      const config = await response.json();
      
      // If apiUrl is provided and not empty, use it; otherwise use localhost
      if (config.apiUrl && config.apiUrl.trim() !== '') {
        API_URL = config.apiUrl;
        console.log('🌐 Using API URL from config.json:', API_URL);
      } else {
        console.log('🏠 Using local API URL:', API_URL);
      }
    } catch (error) {
      console.warn('⚠️ Could not load config.json, using default localhost:', error);
      console.log('🏠 Using local API URL:', API_URL);
    } finally {
      setApiConfigLoaded(true);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API_URL}/statistics`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token) => {
    localStorage.setItem('authToken', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setStatistics(null);
  };

  // Wait for config.json so pages never fire requests at the default localhost API URL
  if (!apiConfigLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex-shrink-0 flex items-center">
                  <h1 className="text-2xl font-bold text-primary-600">
                    📊 Deneme Takip Sistemi
                  </h1>
                </div>
                <div className="ml-6 flex space-x-8">
                  <Link
                    to="/"
                    className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  >
                    Ana Sayfa
                  </Link>
                  <Link
                    to="/upload"
                    className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  >
                    Dosya Yükle
                  </Link>
        {/* Logs navigation removed - logging is now hidden from users */}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {!loading && statistics && statistics.totalExams > 0 && (
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <span className="font-semibold">Toplam Deneme:</span>
                      <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-700 rounded-full">
                        {statistics.totalExams}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold">Öğrenci:</span>
                      <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        {statistics.totalStudents}
                      </span>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleLogout}
                  className="ml-4 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Çıkış
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <Routes>
            <Route path="/" element={<Dashboard onDataUpdate={fetchStatistics} />} />
            <Route path="/upload" element={<UploadPage onUploadSuccess={fetchStatistics} />} />
            <Route path="/student-dashboard/:tcNo" element={<StudentDashboard />} />
            {/* Logs route removed - logging is now hidden from users */}
          </Routes>
        </main>
      </div>
    </Router>
  );
}

// Export function to get current API URL
export const getApiUrl = () => API_URL;

// Auth token for URLs that can't send headers (e.g. <img> tags)
export const getAuthToken = () => localStorage.getItem('authToken') || '';

export default App;

